import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Typography, 
  FormGroup, 
  FormControlLabel, 
  Checkbox, 
  Grid, 
  Box,
  Paper
} from '@mui/material';

const ClockSegment = ({ 
  startAngle, 
  endAngle, 
  selected, 
  onClick, 
  label,
  labelPosition 
}) => {
  const radius = 40;
  const center = 50;
  
  // Convert angles to radians and calculate path
  const start = {
    x: center + radius * Math.cos((startAngle - 90) * Math.PI / 180),
    y: center + radius * Math.sin((startAngle - 90) * Math.PI / 180)
  };
  const end = {
    x: center + radius * Math.cos((endAngle - 90) * Math.PI / 180),
    y: center + radius * Math.sin((endAngle - 90) * Math.PI / 180)
  };
  
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  
  const path = [
    "M", center, center,
    "L", start.x, start.y,
    "A", radius, radius, 0, largeArcFlag, 1, end.x, end.y,
    "Z"
  ].join(" ");

  // Calculate label position
  const labelAngle = (startAngle + endAngle) / 2;
  const labelRadius = radius * 0.6;
  const labelX = center + labelRadius * Math.cos((labelAngle - 90) * Math.PI / 180);
  const labelY = center + labelRadius * Math.sin((labelAngle - 90) * Math.PI / 180);

  return (
    <g 
      onClick={onClick} 
      style={{ 
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'none',
        pointerEvents: 'bounding-box'
      }}
    >
      <path
        d={path}
        fill={selected ? "#1976d2" : "#e0e0e0"}
        stroke="#fff"
        strokeWidth="1"
      />
      <text
        x={labelX}
        y={labelY}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={selected ? "#fff" : "#666"}
        fontSize="8"
        style={{ userSelect: 'none' }}
      >
        {label}
      </text>
    </g>
  );
};

const MobileAvailability = ({
  days,
  timeSlots,
  availability,
  setAvailability,
  t
}) => {
  const [selectedDay, setSelectedDay] = useState(days[0]);

  const dayAbbreviations = {
    monday: 'Mo',
    tuesday: 'Tu',
    wednesday: 'We',
    thursday: 'Th',
    friday: 'Fr',
    saturday: 'Sa',
    sunday: 'Su'
  };

  const timeSlotConfig = {
    morning: { startAngle: -120, endAngle: 0, label: "8-12" },
    day: { startAngle: 0, endAngle: 120, label: "12-16" },
    evening: { startAngle: 120, endAngle: 240, label: "16-20" }
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2, textAlign: 'left' }}>
        {t('profile.availability.mobileExplanation')}
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        mb: 2,
        gap: 0.5
      }}>
        {days.map((day) => (
          <Paper
            key={day}
            elevation={selectedDay === day ? 3 : 1}
            sx={{
              p: 1,
              cursor: 'pointer',
              bgcolor: 'background.paper',
              color: selectedDay === day ? 'primary.main' : 'text.primary',
              flex: 1,
              textAlign: 'center',
              border: selectedDay === day ? 2 : 1,
              borderColor: selectedDay === day ? 'primary.main' : 'divider',
              '&:hover': {
                bgcolor: 'background.paper',
                borderColor: 'primary.main'
              }
            }}
            onClick={() => setSelectedDay(day)}
          >
            <Typography variant="body2">
              {dayAbbreviations[day]}
            </Typography>
          </Paper>
        ))}
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        mt: 1,
        mb: 1
      }}>
        <svg 
          viewBox="0 0 100 100" 
          width="200" 
          height="200"
          style={{ 
            // transform: 'rotate(-90deg)',
            backgroundColor: '#f5f5f5',
            borderRadius: '50%'
          }}
        >
          {timeSlots.map((slot) => (
            <ClockSegment
              key={slot}
              startAngle={timeSlotConfig[slot].startAngle}
              endAngle={timeSlotConfig[slot].endAngle}
              selected={(availability[selectedDay] && availability[selectedDay][slot]) || false}
              onClick={() => {
                const currentValue = (availability[selectedDay] && availability[selectedDay][slot]) || false;
                setAvailability([{
                  day: selectedDay,
                  timeSlot: slot,
                  checked: !currentValue
                }]);
              }}
              label={timeSlotConfig[slot].label}
            />
          ))}
          {/* Clock center dot */}
          <circle cx="50" cy="50" r="2" fill="#666" />
        </svg>
        
        {/* <Box sx={{ mt: 3, textAlign: 'center' }}>
          {timeSlots.map((slot) => (
            <Typography 
              key={slot}
              variant="body1" 
              sx={{ 
                color: (availability[selectedDay] && availability[selectedDay][slot]) ? '#1976d2' : '#666',
                mb: 1
              }}
            >
              {t(`profile.availability.timeSlots.${slot}`)}
            </Typography>
          ))}
        </Box> */}
      </Box>
    </Box>
  );
};

const DesktopAvailability = ({
  days,
  timeSlots,
  availability,
  setAvailability,
  handleDayClick,
  handleSlotClick,
  t
}) => (
  <Box>
    <Grid container spacing={2}>
      <Grid item xs={3}>
        <Typography variant="subtitle1"></Typography>
      </Grid>
      {timeSlots.map((slot) => (
        <Grid item xs={3} key={slot}>
          <Typography variant="subtitle1" 
            sx={{
              margin: 1,
              textAlign: 'center',
              cursor: 'pointer', 
              '&:hover': { textDecoration: 'underline' },
              userSelect: 'none'
            }}
            onClick={() => handleSlotClick(slot)}
          >
            {t(`profile.availability.timeSlots.${slot}`)}
          </Typography>
        </Grid>
      ))}
    </Grid>
    {days.map((day) => (
      <Grid container spacing={2} key={day}>
        <Grid item xs={3}>
          <Typography 
            sx={{
              margin: 1, 
              cursor: 'pointer', 
              '&:hover': { textDecoration: 'underline' },
              userSelect: 'none'
            }}
            onClick={() => handleDayClick(day)}
          >
            {t(`profile.availability.days.${day}`)}
          </Typography>
        </Grid>
        {timeSlots.map((slot) => (
          <Grid item xs={3} key={`${day}-${slot}`} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Checkbox 
              checked={(availability[day] && availability[day][slot]) || false}
              onChange={(event) => setAvailability([{
                day,
                timeSlot: slot,
                checked: event.target.checked
              }])}
            />
          </Grid>
        ))}
      </Grid>
    ))}
  </Box>
);

const ProfileEditorAvailability = ({
  activityPreference = {}, 
  setActivityPreference, 
  availability = {}, 
  setAvailability
}) => {
  const { t } = useTranslation();
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const timeSlots = ['morning', 'day', 'evening'];

  const handleDayClick = (day) => {
    // Check if all slots for the day are selected
    const allChecked = timeSlots.every(slot => 
      availability[day] && availability[day][slot] === true
    );
    
    // Create changes array for all slots
    const changes = timeSlots.map(slot => ({
      day,
      timeSlot: slot,
      checked: !allChecked
    }));
    
    // Submit all changes at once
    setAvailability(changes);
  };

  const handleSlotClick = (slot) => {
    // Check if all days for the slot are selected
    const allChecked = days.every(day => 
      availability[day] && availability[day][slot] === true
    );
    
    // Create changes array for all days
    const changes = days.map(day => ({
      day,
      timeSlot: slot,
      checked: !allChecked
    }));
    
    // Submit all changes at once
    setAvailability(changes);
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 600);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <Box sx={{ width: isMobile ? '100%' : '80%', textAlign: 'left' }}>
      <Typography variant="h6" gutterBottom>
        {t('profile.availability.activityPreference')}
      </Typography>
      <FormGroup sx={{ flexDirection: 'row', justifyContent: 'flex-start' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={activityPreference.cruising || false}
              onChange={setActivityPreference}
              name="cruising"
            />
          }
          label={t('profile.availability.cruising')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={activityPreference.racing || false}
              onChange={setActivityPreference}
              name="racing"
            />
          }
          label={t('profile.availability.racing')}
        />
      </FormGroup>

      <Typography variant="h6" gutterBottom sx={{ marginTop: 3 }}>
        {t('profile.availability.title')}
      </Typography>
      
      {isMobile ? (
        <MobileAvailability
          days={days}
          timeSlots={timeSlots}
          availability={availability}
          setAvailability={setAvailability}
          t={t}
        />
      ) : (
        <DesktopAvailability
          days={days}
          timeSlots={timeSlots}
          availability={availability}
          setAvailability={setAvailability}
          handleDayClick={handleDayClick}
          handleSlotClick={handleSlotClick}
          t={t}
        />
      )}

    </Box>
  );
};

export default ProfileEditorAvailability;
